import React, { useState } from 'react';
import { TextField } from '@anyemp/ui-kit';
import {
  Bot,
  Plus,
  Trash2,
  AlertCircle,
  X,
  Clock
} from 'lucide-react';

const AISettingsManager = ({
  entityId,
  entityType = 'Talent',
  aiSettings = [],
  onAISettingsChange,
  showTitle = true,
  title = "AI Settings",
  className = "",
  allowAddRemove = true
}) => {
  const [error, setError] = useState(null);

  const addAISetting = () => {
    const newSetting = {
      id: Date.now(),
      ai_voice_agent_id: '',
      created_at: new Date().toISOString()
    };

    onAISettingsChange([...aiSettings, newSetting]);
  };

  const removeAISetting = (index) => {
    onAISettingsChange(aiSettings.filter((_, i) => i !== index));
  };

  const updateAISetting = (index, field, value) => {
    const updatedSettings = aiSettings.map((setting, i) => {
      if (i === index) {
        return { ...setting, [field]: value };
      }
      return setting;
    });
    onAISettingsChange(updatedSettings);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`bg-light-bg-primary dark:bg-dark-bg-secondary shadow rounded-lg p-6 border border-light-border-primary dark:border-dark-border-primary ${className}`}>
      {showTitle && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary flex items-center">
            <Bot className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
            {title}
          </h3>
          {allowAddRemove && (
            <button
              type="button"
              onClick={addAISetting}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add AI Setting
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-300 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-3 inline-flex items-center justify-center h-5 w-5 text-red-400 dark:text-red-300 hover:text-red-600 dark:hover:text-red-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {aiSettings.length === 0 ? (
        <div className="text-center py-8">
          <Bot className="h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary mx-auto mb-4" />
          <p className="mt-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">No AI settings added</p>
          <p className="mt-1 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">Click "Add AI Setting" to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {aiSettings.map((setting, index) => (
            <div
              key={setting.id || index}
              className="border rounded-lg p-4 transition-colors border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-tertiary"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2">
                  <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                    AI Setting {index + 1}
                  </span>
                </div>
                {allowAddRemove && (
                  <button
                    type="button"
                    onClick={() => removeAISetting(index)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                    title="Remove AI setting"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <TextField
                    label="AI Voice Agent ID"
                    fullWidth
                    size="small"
                    type="text"
                    value={setting.ai_voice_agent_id || ''}
                    onChange={(e) => updateAISetting(index, 'ai_voice_agent_id', e.target.value || null)}
                    placeholder="Enter AI Voice Agent ID (optional)"
                  />
                </div>

                {setting.created_at && (
                  <div className="flex items-end">
                    <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      Created: {formatDateTime(setting.created_at)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {aiSettings.length > 0 && (
        <div className="mt-4 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
          {aiSettings.length} AI setting(s)
        </div>
      )}
    </div>
  );
};

export default AISettingsManager;
